import { Box, Cyl, RBox, Slab } from '../../render/parts'
import type { Tone } from '../../render/theme'

/** Screens, keyboards and desk electronics. Sized from real 24"/27" hardware. */

export interface ScreenPanelProps {
  width: number
  height: number
  /** Bezel thickness around the display area. */
  bezel?: number
  frameTone?: Tone
  screenTone?: Tone
  screenColor?: string
  thickness?: number
}

/** A flat display: chassis plus an inset screen face. Shared by monitors and TVs. */
export function ScreenPanel({
  width,
  height,
  bezel = 0.014,
  frameTone = 'shell',
  screenTone = 'screen',
  screenColor,
  thickness = 0.022,
}: ScreenPanelProps) {
  return (
    <group>
      <Slab
        tone={frameTone}
        size={[width, height, thickness]}
        radius={0.01}
        rotation={[Math.PI / 2, 0, 0]}
      />
      <Slab
        tone={screenTone}
        color={screenColor}
        size={[width - bezel * 2, height - bezel * 2, 0.004]}
        radius={0.006}
        rotation={[Math.PI / 2, 0, 0]}
        position={[0, 0, thickness / 2 + 0.001]}
      />
    </group>
  )
}

export interface MonitorProps {
  /** Diagonal-ish sizing shortcut: display width in metres. */
  width?: number
  height?: number
  tone?: Tone
  screenTone?: Tone
  screenColor?: string
  /** Height of the desk surface the monitor stands on. */
  base?: number
  tilt?: number
}

/** Desktop monitor on a T-stand. Total height ≈ 0.46 above the surface. */
export function Monitor({
  width = 0.55,
  height = 0.33,
  tone = 'shell',
  screenTone = 'screen',
  screenColor,
  base = 0,
  tilt = -0.06,
}: MonitorProps) {
  const standHeight = 0.14
  const centre = base + standHeight + height / 2 + 0.02
  return (
    <group>
      <Slab tone={tone} size={[0.22, 0.16, 0.012]} radius={0.03} position={[0, base + 0.006, -0.02]} />
      <Box tone={tone} size={[0.055, standHeight, 0.03]} position={[0, base + standHeight / 2, -0.02]} />
      <group position={[0, centre, 0]} rotation={[tilt, 0, 0]}>
        <ScreenPanel width={width} height={height} frameTone={tone} screenTone={screenTone} screenColor={screenColor} />
      </group>
    </group>
  )
}

export interface WallDisplayProps {
  width?: number
  height?: number
  tone?: Tone
  screenTone?: Tone
  screenColor?: string
}

/** Wall-mounted presentation display. Origin at its centre. */
export function WallDisplay({
  width = 1.35,
  height = 0.78,
  tone = 'darkSoft',
  screenTone = 'screen',
  screenColor,
}: WallDisplayProps) {
  return (
    <group>
      <ScreenPanel
        width={width}
        height={height}
        bezel={0.02}
        thickness={0.045}
        frameTone={tone}
        screenTone={screenTone}
        screenColor={screenColor}
      />
      <Box tone="metalDark" size={[0.24, 0.24, 0.03]} position={[0, 0, -0.035]} />
    </group>
  )
}

export interface LaptopProps {
  tone?: Tone
  screenTone?: Tone
  screenColor?: string
  /** 0 = closed, 1 = fully open. */
  open?: number
  base?: number
}

export function Laptop({ tone = 'shell', screenTone = 'screen', screenColor, open = 1, base = 0 }: LaptopProps) {
  const width = 0.33
  const depth = 0.23
  const deck = 0.014
  const lid = 0.009
  const hingeZ = -depth / 2 + 0.006
  const lidLength = depth - 0.012

  /*
   * The lid pivots about the hinge at the back edge. It lies flat on the deck at
   * 0 and swings UP by rotating negatively about X (Rx maps a point at +Z to
   * y = -L·sinθ), so a negative angle lifts it. 1.92 rad puts the screen just
   * past vertical, leaning back the way an open laptop actually sits.
   */
  const lidAngle = -open * 1.92

  return (
    <group position={[0, base, 0]}>
      <Slab tone={tone} size={[width, depth, deck]} radius={0.008} position={[0, deck / 2, 0]} />
      {/* keyboard nearest the hinge, trackpad nearest the user */}
      <Slab tone="shellSoft" size={[width * 0.84, depth * 0.4, 0.002]} radius={0.004} position={[0, deck + 0.001, -0.028]} />
      <Slab tone="shellSoft" size={[width * 0.3, depth * 0.26, 0.002]} radius={0.004} position={[0, deck + 0.001, 0.062]} />

      <group position={[0, deck, hingeZ]} rotation={[lidAngle, 0, 0]}>
        <group position={[0, 0, lidLength / 2]}>
          <Slab tone={tone} size={[width, lidLength, lid]} radius={0.008} />
          {/* The face that pointed DOWN at the deck when closed becomes the
              screen once the lid is up, so the display sits on local -Y. */}
          <Slab
            tone={screenTone}
            color={screenColor}
            size={[width - 0.022, lidLength - 0.022, 0.003]}
            radius={0.005}
            position={[0, -lid / 2 - 0.0012, 0]}
          />
        </group>
      </group>
    </group>
  )
}

export function Keyboard({ tone = 'shell', base = 0 }: { tone?: Tone; base?: number }) {
  return (
    <group position={[0, base, 0]}>
      <Slab tone={tone} size={[0.42, 0.135, 0.014]} radius={0.008} position={[0, 0.007, 0]} />
      <Slab tone="shellSoft" size={[0.39, 0.105, 0.003]} radius={0.005} position={[0, 0.0155, 0]} />
    </group>
  )
}

export function Mouse({ tone = 'shell', base = 0 }: { tone?: Tone; base?: number }) {
  return <Slab tone={tone} size={[0.058, 0.098, 0.026]} radius={0.028} position={[0, base + 0.013, 0]} />
}

export function DeskPhone({ tone = 'darkSoft', base = 0 }: { tone?: Tone; base?: number }) {
  return (
    <group position={[0, base, 0]}>
      <Slab tone={tone} size={[0.19, 0.17, 0.035]} radius={0.012} position={[0, 0.018, 0]} rotation={[0.12, 0, 0]} />
      <Slab tone={tone} size={[0.055, 0.19, 0.032]} radius={0.016} position={[0, 0.05, 0.005]} />
    </group>
  )
}

export interface PrinterProps {
  tone?: Tone
  accent?: string
  base?: number
}

/** Compact multifunction printer. Footprint 0.5 x 0.45, height 0.36. */
export function Printer({ tone = 'shell', accent, base = 0 }: PrinterProps) {
  return (
    <group position={[0, base, 0]}>
      <RBox tone={tone} size={[0.5, 0.24, 0.45]} radius={0.014} position={[0, 0.12, 0]} />
      <RBox tone="shellSoft" size={[0.5, 0.075, 0.45]} radius={0.014} position={[0, 0.28, 0]} />
      <Slab tone="darkSoft" size={[0.36, 0.02, 0.006]} radius={0.003} position={[0, 0.238, 0.226]} />
      <Slab tone="accent" color={accent} size={[0.09, 0.05, 0.006]} radius={0.004} rotation={[Math.PI / 2, 0, 0]} position={[0.16, 0.31, 0.19]} />
      <Slab tone="shell" size={[0.4, 0.16, 0.008]} radius={0.006} position={[0, 0.335, 0.06]} rotation={[0.14, 0, 0]} />
    </group>
  )
}

export function SpeakerPuck({ tone = 'darkSoft', base = 0 }: { tone?: Tone; base?: number }) {
  return (
    <group position={[0, base, 0]}>
      <Cyl tone={tone} r={0.06} h={0.03} position={[0, 0.015, 0]} segments={22} />
      <Cyl tone="metalDark" r={0.045} h={0.004} position={[0, 0.032, 0]} segments={22} />
    </group>
  )
}
